import React from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { VariantHistoryRow } from '@/shared/types/variantHistory';
import {
  formatAllTimeGmv,
  formatDaysUntilStockout,
  formatMoney,
  formatPercent,
  formatSnapshotDate,
  formatWholeNumber,
  notRecordedText,
} from './variantHistoryFormat';

const HEADERS = [
  'Data as of',
  '∑: GMV',
  '∑: NMV',
  'Fees',
  'Units (12 Mnth)',
  'Inv Available',
  'Days Until Stockout',
  '∑: GEV',
  '∑: NEV',
  'DR: SKU %',
  '∑: NERV',
  'GMV (All Time)',
] as const;

interface VariantHistoryTableProps {
  rows: VariantHistoryRow[];
  /** The snapshot date being downloaded, if any. */
  downloading: string | null;
  /** The stored days are the previous page or filter while the next one loads. */
  stale: boolean;
  onDownload: (row: VariantHistoryRow) => void;
}

/** The day rows: recorded days download on click, Not recorded days are greyed with their reason (FIG-239). */
export const VariantHistoryTable: React.FC<VariantHistoryTableProps> = ({ rows, downloading, stale, onDownload }) => (
  <div className={`overflow-x-auto rounded-lg border ${stale ? 'opacity-60' : ''}`} aria-busy={stale}>
    <Table className="min-w-[1500px] tabular-nums">
      <TableHeader>
        <TableRow>
          {HEADERS.map((header) => (
            <TableHead key={header} className={header === 'GMV (All Time)' ? 'font-semibold' : undefined}>
              {header}
            </TableHead>
          ))}
          <TableHead className="w-12">
            <span className="sr-only">Download</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const date = formatSnapshotDate(row.snapshotDate);
          if (row.status !== 'recorded' || !row.figures) {
            return (
              <TableRow key={row.snapshotDate} className="bg-muted/40 text-muted-foreground hover:bg-muted/40" data-testid="variant-history-row-not-recorded">
                <TableCell className="font-medium">{date}</TableCell>
                <TableCell colSpan={HEADERS.length}>
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-700" aria-hidden="true" />
                    {notRecordedText(row.reason)}
                  </span>
                </TableCell>
              </TableRow>
            );
          }
          const f = row.figures;
          const busy = downloading === row.snapshotDate;
          return (
            <TableRow
              key={row.snapshotDate}
              role="button"
              tabIndex={0}
              aria-label={`Download the Variants export for ${date}`}
              title="Download CSV"
              aria-busy={busy}
              className="cursor-pointer"
              data-testid="variant-history-row-recorded"
              onClick={() => onDownload(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onDownload(row);
                }
              }}
            >
              <TableCell className="font-medium">{date}</TableCell>
              <TableCell>{formatMoney(f.gmv)}</TableCell>
              <TableCell>{formatMoney(f.nmv)}</TableCell>
              <TableCell>{formatMoney(f.fees)}</TableCell>
              <TableCell>{formatWholeNumber(f.units)}</TableCell>
              <TableCell>{formatWholeNumber(f.invAvailable)}</TableCell>
              <TableCell>{formatDaysUntilStockout(f.daysUntilStockout)}</TableCell>
              <TableCell>{formatMoney(f.gev)}</TableCell>
              <TableCell>{formatMoney(f.nev)}</TableCell>
              <TableCell>{formatPercent(f.drSkuPercentage)}</TableCell>
              <TableCell>{formatMoney(f.nerv)}</TableCell>
              <TableCell className="font-semibold">{formatAllTimeGmv(f.gmvAllTime)}</TableCell>
              <TableCell>
                {busy ? (
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="mx-auto h-4 w-4 text-indigo-600" aria-hidden="true" />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);
